/**
 * LocationPicker — the ONE shared clinic-list UI.
 *
 * Used by both the first-visit LocationGate and the persistent header
 * LocationSelector (no copy-paste of the list). Also exports the shared
 * `LocationDialog` shell (backdrop + focus trap + ESC) and the `useGeolocate`
 * hook that wraps navigator.geolocation with full failure handling.
 *
 * Every user-facing string comes from the i18n dictionary (locationGate.*) —
 * scripts/verify-i18n-completeness.js forbids bare English JSX text.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { FaMapMarkerAlt, FaCrosshairs, FaTimes, FaCheck } from "react-icons/fa";
import { t } from "../../i18n";
import { nearestLocation, physicalSiteFor } from "../../utils/locationResolver";
import "./LocationPicker.css";

const GEO_TIMEOUT_MS = 8000;

/**
 * Geolocation attempt with hard guarantees: never leaves a permanent spinner,
 * handles "API missing", "permission denied" and "timeout" distinctly, and
 * always resolves back to the manual list on failure.
 */
export function useGeolocate(locations, onResolved) {
  const [busy, setBusy] = useState(false);
  const [errorKey, setErrorKey] = useState(null);
  const cancelled = useRef(false);

  useEffect(() => () => { cancelled.current = true; }, []);

  const locate = useCallback(() => {
    setErrorKey(null);
    const geo =
      typeof navigator !== "undefined" && navigator.geolocation
        ? navigator.geolocation
        : null;
    if (!geo) {
      setErrorKey("locationGate.geoUnsupported");
      return;
    }
    setBusy(true);
    let settled = false;
    const finish = (key) => {
      if (settled || cancelled.current) return;
      settled = true;
      setBusy(false);
      if (key) setErrorKey(key);
    };
    // Belt-and-braces watchdog: some mobile browsers never fire either callback.
    const watchdog = setTimeout(() => finish("locationGate.geoTimeout"), GEO_TIMEOUT_MS + 1500);
    geo.getCurrentPosition(
      (pos) => {
        clearTimeout(watchdog);
        const best = nearestLocation(
          pos?.coords?.latitude,
          pos?.coords?.longitude,
          locations
        );
        if (!best) {
          finish("locationGate.geoUnavailable");
          return;
        }
        finish(null);
        if (typeof onResolved === "function") {
          onResolved(best.location.slug, Math.round(best.distanceMiles));
        }
      },
      (err) => {
        clearTimeout(watchdog);
        const code = err && err.code;
        if (code === 1) finish("locationGate.geoDenied");
        else if (code === 3) finish("locationGate.geoTimeout");
        else finish("locationGate.geoUnavailable");
      },
      { enableHighAccuracy: false, timeout: GEO_TIMEOUT_MS, maximumAge: 300000 }
    );
  }, [locations, onResolved]);

  return { locate, busy, errorKey };
}

/**
 * Focus trap + initial focus + (optionally) ESC-to-close for a modal dialog.
 *
 * `dismissible: false` is used by the MANDATORY first-visit gate: a visitor
 * must commit to a clinic before browsing, so ESC must not close it. Everything
 * else about the dialog stays fully accessible — focus is trapped, the clinic
 * list is keyboard-reachable and labelled. Mandatory != inaccessible: there is
 * always a way OUT (choose a clinic), it simply cannot be SKIPPED.
 */
export function useDialogBehavior(onClose, dismissible = true) {
  const ref = useRef(null);
  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;
    const previouslyFocused = document.activeElement;
    const focusables = () =>
      Array.from(
        node.querySelectorAll(
          'button:not([disabled]),[href],input,select,textarea,[tabindex]:not([tabindex="-1"])'
        )
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
    const first = focusables()[0];
    if (first) first.focus();
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        if (!dismissible) {
          // Swallow it so no ancestor handler can close the gate either.
          e.stopPropagation();
          e.preventDefault();
          return;
        }
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const items = focusables();
      if (!items.length) return;
      const firstItem = items[0];
      const lastItem = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstItem) {
        e.preventDefault();
        lastItem.focus();
      } else if (!e.shiftKey && document.activeElement === lastItem) {
        e.preventDefault();
        firstItem.focus();
      }
    };
    node.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      node.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
      if (previouslyFocused && previouslyFocused.focus) previouslyFocused.focus();
    };
  }, [onClose, dismissible]);
  return ref;
}

/** Shared modal shell: dim backdrop, centered on-brand glass card, mobile-first. */
/**
 * Modal shell. ALWAYS portaled to document.body.
 *
 * WHY THE PORTAL IS LOAD-BEARING (2026-08-12 bug): the header LocationSelector
 * renders this dialog from inside <header class="site-header">, which carries
 * `backdrop-filter: blur(20px)`. A filter/backdrop-filter/transform makes an
 * element the CONTAINING BLOCK for its `position: fixed` descendants, so the
 * backdrop's `inset: 0` resolved to the 72px-tall header instead of the
 * viewport. Centering a ~662px dialog inside a 72px box pushed it to
 * top: -295px, cutting the title and "Use my location" off the top of the
 * screen with no way to reach them. The first-visit gate was unaffected only
 * because it mounts at app level, outside the header.
 *
 * Portaling to document.body escapes any ancestor containing block, so the
 * dialog is viewport-centred no matter where it is rendered from. Do not remove.
 *
 * `mandatory` (2026-08-12): renders the shell as an un-skippable gate — no
 * backdrop-click close, no ESC, and NO close button. Used only when the visitor
 * has no clinic yet; the only exit is choosing one.
 */
export function LocationDialog({ locale, titleKey, onClose, children, variant, mandatory, headerExtra }) {
  const dismissible = !mandatory;
  const dialogRef = useDialogBehavior(onClose, dismissible);
  const titleId = `loc-dialog-title-${variant || "gate"}`;

  // NOTE (2026-08-12): background scroll is locked by `useDialogBehavior` and
  // ONLY there. A second component-level lock used to live here, and the pair
  // leaked a permanent `body { overflow: hidden }`:
  //
  //   React runs an unmount's cleanups in DECLARATION order, so the inner hook
  //   restored the original value FIRST and this effect then restored the value
  //   IT had captured — which was already "hidden" (the inner hook had set it
  //   before this effect ran). Net result: the page could never scroll again
  //   after the dialog closed. Verified in jsdom.
  //
  // That was survivable when the dialog was optional; it is fatal for the
  // mandatory gate, whose entire purpose is to let the visitor PROCEED after
  // choosing. One owner of the lock, always. Do not re-add a second one.

  const markup = (
    <div
      className={`locdlg-backdrop${mandatory ? " locdlg-backdrop-mandatory" : ""}`}
      onMouseDown={(e) => {
        if (!dismissible) return;
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className={`locdlg locdlg-${variant || "gate"}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        ref={dialogRef}
      >
        <div className="locdlg-head">
          <h2 className="locdlg-title" id={titleId}>
            <FaMapMarkerAlt aria-hidden="true" /> {t(titleKey, locale)}
          </h2>
          {dismissible && (
            <button
              type="button"
              className="locdlg-close"
              onClick={onClose}
              aria-label={t("locationGate.close", locale)}
            >
              <FaTimes aria-hidden="true" />
            </button>
          )}
        </div>
        {headerExtra}
        {children}
      </div>
    </div>
  );

  // Guard the prerender pass, where document does not exist.
  if (typeof document === "undefined" || !document.body) return markup;
  return createPortal(markup, document.body);
}

/**
 * The shared list UI: "Use my location" + an always-visible, always-selectable
 * list of the brand's clinics (city, state, address, phone when known).
 * The manual list is what makes selection unavoidable when geolocation fails —
 * the user can never be silently dropped into a wrong-city default.
 */
export default function LocationPicker({
  locale,
  brand,
  locations,
  selectedSlug,
  onSelect,
  descriptionKey,
  suggestedSlug: suggestedSlugProp,
  onLocate,
  locating = false,
}) {
  const handleGeoResolved = useCallback(
    (slug, miles) => onSelect(slug, { miles, source: "geo" }),
    [onSelect]
  );
  const internal = useGeolocate(locations, handleGeoResolved);

  // The mandatory gate supplies its own `onLocate` because it must send the
  // coordinates to thor-geo and then ask the visitor to CONFIRM the answer,
  // rather than resolve-and-commit locally the way this hook does. Same single
  // button either way — no duplicated affordance, and geolocation is still only
  // ever requested by an explicit press.
  const controlled = typeof onLocate === "function";
  const locate = controlled ? onLocate : internal.locate;
  const busy = controlled ? locating : internal.busy;
  const errorKey = controlled ? null : internal.errorKey;

  // The suggested clinic is supplied ONLY by the parent, and only ever from the
  // explicit "Use my location" flow that passes real coordinates to thor-geo
  // (the mandatory LocationGate runs that flow and hands the winner down as
  // `suggestedSlug`). There is no coord-less pre-fetch: a request without
  // coordinates could only ever answer `resolved:false`, so it was dead weight
  // and has been removed. No suggestion appears until the visitor asks for one.
  const suggestedSlug = suggestedSlugProp || null;

  // Show the suggested clinic first so it is the obvious tap target on mobile,
  // while every other clinic stays present and selectable.
  const ordered = useMemo(() => {
    if (!suggestedSlug) return locations;
    const hit = locations.find((l) => l.slug === suggestedSlug);
    if (!hit) return locations;
    return [hit, ...locations.filter((l) => l.slug !== suggestedSlug)];
  }, [locations, suggestedSlug]);

  return (
    <div className="locpick">
      {descriptionKey && (
        <p className="locpick-desc">{t(descriptionKey, locale)}</p>
      )}

      <button
        type="button"
        className="locpick-geo glass-btn glass-btn-primary"
        onClick={locate}
        disabled={busy}
        aria-busy={busy ? "true" : "false"}
      >
        <FaCrosshairs aria-hidden="true" />{" "}
        {busy ? t("locationGate.locating", locale) : t("locationGate.useMyLocation", locale)}
      </button>

      {errorKey && (
        <p className="locpick-note" role="status">
          {t(errorKey, locale)}
        </p>
      )}

      <p className="locpick-or" id="locpick-list-label">
        {t("locationGate.orChoose", locale)}
      </p>

      <ul className="locpick-list" aria-labelledby="locpick-list-label">
        {ordered.map((l) => {
          const site = physicalSiteFor(brand, l);
          const active = l.slug === selectedSlug;
          const suggested = !active && l.slug === suggestedSlug;
          return (
            <li key={l.slug}>
              <button
                type="button"
                className={`locpick-item${active ? " is-active" : ""}${suggested ? " is-suggested" : ""}`}
                onClick={() => onSelect(l.slug, { source: suggested ? "geoip-confirmed" : "manual" })}
                aria-current={active ? "true" : undefined}
              >
                <span className="locpick-item-main">
                  <span className="locpick-city">
                    {l.city}, {l.state}
                    {suggested && (
                      <span className="locpick-suggested">
                        {t("locationGate.suggested", locale)}
                      </span>
                    )}
                  </span>
                  {site && site.addr1 && (
                    <span className="locpick-addr">
                      {site.addr1}
                      {site.zip ? ` · ${site.zip}` : ""}
                    </span>
                  )}
                  {/*
                   * Option A (owner-confirmed 2026-08-27): every location card
                   * shows the brand hero/main phone, NOT per-office DIDs. This
                   * matches what HomeHero renders. Per-office DIDs remain in
                   * structured-data/schema markup via utils/sitePhone.js
                   * (unchanged). Do not swap this for physicalSite.phone or
                   * sitePhone(brand, l) without a fresh owner decision. See
                   * audits/ortho-location-modal-phone-inventory-20260827.md.
                   *
                   * Rendered as a plain span (not an anchor) because the whole
                   * card is a <button>; nesting an <a> inside <button> is
                   * invalid HTML and traps the click target for keyboard users.
                   */}
                  {brand && brand.phone && (
                    <span className="locpick-phone">{brand.phone}</span>
                  )}
                </span>
                {active && <FaCheck className="locpick-check" aria-hidden="true" />}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
